import { Box, Stack, type SxProps } from "@mui/material";
import { groupBy } from "lodash";
import { useMemo } from "react";

interface GroupedListProps<T extends { key: string }> {
  items: T[];
  groupBy: (item: T) => string;
  renderGroupHeader: (groupKey: string, itemCount: number) => React.ReactNode;
  renderItem: (item: T) => React.ReactNode;
  renderListHeader?: () => React.ReactNode;
  sortGroupKeys?: (keyA: string, keyB: string) => number;
  sx?: SxProps;
}

export function GroupedFiniteList<T extends { key: string }>(props: GroupedListProps<T>) {
  const {
    items,
    groupBy: groupByFunction,
    renderGroupHeader,
    renderItem,
    renderListHeader,
    sortGroupKeys,
    sx,
  } = props;

  const group = useMemo(() => {
    const itemsByGroup = groupBy(items, groupByFunction);
    const groupKeys = Object.keys(itemsByGroup).sort(sortGroupKeys);
    const counts = groupKeys.map((key) => itemsByGroup[key]?.length ?? 0);
    return { counts, groupKeys, itemsByGroup };
  }, [items, groupByFunction, sortGroupKeys]);

  return (
    <Stack spacing={7} sx={{ ...sx }}>
      {renderListHeader?.()}
      {group.groupKeys.map((groupKey, groupIndex) => {
        const groupItems = group.itemsByGroup[groupKey] ?? [];
        const itemCount = group.counts[groupIndex];
        return (
          <Stack key={groupKey} spacing={5}>
            {renderGroupHeader(groupKey, itemCount)}
            <Stack spacing={5}>
              {groupItems.map((item) => (
                <Box key={`${groupKey}-${item.key}`}>{renderItem(item)}</Box>
              ))}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}
